/* eslint-disable react-native/no-inline-styles */
import React, { useState, useEffect, useMemo } from 'react';
import { StyleSheet, View, TouchableOpacity, ScrollView } from 'react-native';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, FieldArray } from 'formik';

import ModalFull from '@components/modal/ModalFull';
import SociollaText from '@components/sociolla/SociollaText';
import Input from '@components/form/Input';
import * as Layout from '@components/screen/Layout';
import Image from '@components/global/Image';
import RenderIf from '@components/global/RenderIf';
import Button from '@components/form/Button';
import SociollaErrorMessage from '@components/sociolla/form/SociollaErrorMessage';
import RoleSearch from '@components/form/search/RoleSearch';
import CustomerClusterSearch from '@components/form/search/CustomerClusterSearch';
import { SociollaMarketTypeSearch } from '@components/sociolla/form';
import SelectBox from '@components/form/SelectBox';
import { YES_NO_STATUS } from '@/constants';
import { budget_pocket_type } from '@/constants';

import Colors from '@global/colors';
import { isWeb } from '@global/screens';
import { generateConstant } from '@global/helpers';
import useApi from '@/hooks/useApi';
import { MsB2b } from '@api/ms-b2b';

const validationSchema = Yup.object().shape({
  approver: Yup.array().of(
    Yup.object().shape({
      level: Yup.number(),
      role: Yup.mixed().label('Role').required('Role is required'),
    }),
  ),
  min_amount: Yup.number().when(
    ['type', 'min_amount_label', 'max_amount_label'],
    (type, min_amount_label, max_amount_label) => {
      if (type == 'amount') {
        return Yup.number()
          .typeError(min_amount_label + ' should number only')
          .label(min_amount_label)
          .test(
            'maxs',
            min_amount_label + ' should less than ' + max_amount_label,
            function checkMax(value) {
              return value < this.parent.max_amount;
            },
          )
          .required(min_amount_label + ' is required');
      }
      return Yup.number();
    },
  ),
  max_amount: Yup.number().when(
    ['type', 'max_amount_label', 'min_amount_label'],
    (type, max_amount_label, min_amount_label) => {
      if (type == 'amount') {
        return Yup.number()
          .typeError(max_amount_label + ' should number only')
          .test(
            'mins',
            max_amount_label + ' should more than ' + min_amount_label,
            function checkMin(value) {
              return value > this.parent.min_amount;
            },
          )
          .label(max_amount_label)
          .required(max_amount_label + ' is required');
      }
      return Yup.number();
    },
  ),
  applicable_on: Yup.mixed().when(
    ['type', 'applicable_on_label'],
    (type, applicable_on_label) => {
      if (type == 'role') {
        return Yup.mixed()
          .required(applicable_on_label + ' is required')
          .label(applicable_on_label);
      }
      return Yup.mixed();
    },
  ),
  type: Yup.string(),
  min_amount_label: Yup.string(),
  max_amount_label: Yup.string(),
  applicable_on_label: Yup.string(),
  tier_type: Yup.string().when('applicable_for', (applicable_for) => {
    if (applicable_for === 'sales_activity') {
      return Yup.string().required('Tier is required');
    }
    return Yup.string();
  }),
});

const getRole = (role) => {
  const roles = role.split('_');
  for (let i = 0; i < roles.length; i++) {
    roles[i] = roles[i][0].toUpperCase() + roles[i].substr(1);
  }
  return roles.join(' ');
};

const CUSTOMER_TYPE = [
  { value: 'All', label: 'All' },
  { value: 'MT', label: 'MT' },
  { value: 'GT', label: 'GT' },
  { value: 'Subdistributor', label: 'Sub Distributor' },
];

const TIER_TYPE_OPTIONS = [
  { value: 'add', label: 'Add' },
  { value: 'reschedule', label: 'Reschedule' },
];

// Reusable row wrapper: selectbox takes fixed width, right slot is 30px (visible or invisible)
const FieldRow = ({ children, actionSlot }) => (
  <View style={{ flexDirection: 'row', alignItems: 'center' }}>
    <View style={{ width: SELECTBOX_WIDTH }}>{children}</View>
    <View style={rowStyles.actionSlot}>{actionSlot || null}</View>
  </View>
);

const SELECTBOX_WIDTH = isWeb ? 326 : '85%';

FieldRow.propTypes = {
  children: PropTypes.node.isRequired,
  actionSlot: PropTypes.node,
};

FieldRow.defaultProps = {
  actionSlot: null,
};

const rowStyles = StyleSheet.create({
  actionSlot: {
    width: 30,
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 36,
  },
});

const ModalTier = (props) => {
  const [approvers, setApprovers] = useState([
    { label: 'Approval 1', level: 1 },
  ]);
  const [businessTypes, setBusinessTypes] = useState([]);
  const getMarketTypeDetail = useApi(MsB2b.getMarketTypeDetail);

  const getMarketType = async (id) => {
    try {
      const marketTypeDetail = await getMarketTypeDetail.request(id);
      const businessType = (marketTypeDetail?.data?.data?.business_type || [])
        .filter((bt) => bt.is_active)
        .map((bt) => ({ id: bt._id, code: bt.code }));
      setBusinessTypes(businessType);
    } catch (error) {
      console.log(error);
    }
  };

  const initialValues = useMemo(() => {
    const yup = generateConstant(
      'matrix_approval',
      props.data.applicable_for,
      'value',
    );
    const selected = {
      min_amount: '',
      max_amount: '',
      customer_market_type: { id: '', code: '' },
      business_type: [],
      payment_overdue: '',
      applicable_on: '',
      tier_type: 'add',
      applicable_for: props.data.applicable_for,
      approver: [{ level: 1, role: '' }],
      type: props.data.type,
      ...yup.yup,
    };
    if (props.index > -1) {
      const sel = props.data.rule[props.index];
      selected.min_amount = sel.min_amount;
      selected.max_amount = sel.max_amount;
      if (
        props.data.applicable_for === 'order_return_approval' ||
        props.data.applicable_for === 'skp_approval' ||
        props.data.applicable_for === 'sales_order' ||
        props.data.applicable_for === 'request_product_tester'
      ) {
        selected.customer_market_type = sel.customer_market_type;
      }
      if (props.data.applicable_for === 'skp_approval') {
        selected.customer_type = sel.customer_type;
      }
      if (props.data.applicable_for === 'sales_order') {
        selected.payment_overdue = sel.payment_overdue;
        selected.business_type = sel.customer_market_type?.business_type?.length
          ? sel.customer_market_type.business_type
          : [];
      }
      if (yup?.listing?.length > 0) {
        const selec = yup.listing.find(
          (ls) => ls.value == (sel.applicable_on || ''),
        );
        selected.applicable_on = selec ? selec : '';
      } else {
        if (props.data.applicable_for === 'customer_cluster') {
          selected.applicable_on = sel.applicable_on
            ? {
                name: sel.applicable_on,
                _id:
                  props.cluster.find((cl) => cl.name === sel.applicable_on)
                    ?._id || '',
              }
            : '';
        } else {
          selected.applicable_on = sel.applicable_on
            ? { code: sel.applicable_on, name: getRole(sel.applicable_on) }
            : '';
        }
      }
      selected.approver = sel.approver.map((ap) => ({
        level: ap.level,
        role: { code: ap.role, name: getRole(ap.role) },
      }));
      if (props.data.applicable_for === 'sales_activity') {
        selected.tier_type = sel.tier_type || 'add';
      }
    }
    if (
      props.data.type == 'amount' &&
      props.data.applicable_for != 'budget_pocket'
    ) {
      delete selected.applicable_on;
    } else {
      if (props.data.applicable_for !== 'sales_order') {
        delete selected.min_amount;
      }
      delete selected.max_amount;
    }
    return selected;
  }, [props.data, props.index]);

  const customFilterApprover = useMemo(() => {
    if (
      props.data.applicable_for &&
      props.data.applicable_for === 'sales_order'
    ) {
      return {};
    } else {
      return { code: { $nin: ['superadmin', 'salesman', 'general'] } };
    }
  }, [props.data]);

  const tier = useMemo(() => {
    const type = generateConstant(
      'matrix_approval',
      props.data.applicable_for,
      'value',
    );
    return type ? type : null;
  }, [props.data.applicable_for]);

  const isEdit = props.index > -1;

  const addApprover = () => {
    setApprovers((prevState) => {
      const last = prevState[prevState.length - 1].level;
      return [
        ...prevState,
        { label: 'Approval ' + (last + 1), level: last + 1 },
      ];
    });
  };

  const removeApprover = (idx) => {
    setApprovers((prevState) => {
      const filtered = prevState.filter((_, i) => i !== idx);
      return filtered.map((_, i) => ({
        label: 'Approval ' + (i + 1),
        level: i + 1,
      }));
    });
  };

  const handleSubmit = (data) => {
    props.handleTier({ ...data, tier });
  };

  useEffect(() => {
    if (props.index > -1) {
      const selected = props.data.rule[props.index];
      selected.approver.forEach((ap, index) =>
        index > 0 ? addApprover() : false,
      );
      if (
        props.data.applicable_for === 'sales_order' &&
        selected?.customer_market_type?.id
      ) {
        getMarketType(selected?.customer_market_type.id);
      }
    }
  }, [props.index]);

  return (
    <ModalFull
      show={props.show}
      title={isEdit ? 'Edit Tier' : 'Add Tier'}
      width={isWeb ? 406 : '100%'}
      onClose={() => props.onClose()}>
      <View style={styles.container}>
        <ScrollView>
          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}>
            {({ setFieldValue, errors, touched, values, handleSubmit }) => (
              <>
                <Layout.FormWrapper
                  style={{ marginBottom: 15, flexDirection: 'column' }}>
                  <RenderIf
                    condition={[
                      'order_return_approval',
                      'sales_order',
                      'request_product_tester',
                    ].includes(props.data.applicable_for)}>
                    <Layout.FormGroup>
                      <View style={styles.formBottom}>
                        <FieldRow>
                          <SociollaMarketTypeSearch
                            name="market_type"
                            value={values.customer_market_type || ''}
                            onChange={(val) => {
                              const businessType = (val.business_type || [])
                                .filter((bt) => bt.is_active)
                                .map((bt) => ({ id: bt._id, code: bt.code }));
                              setBusinessTypes(businessType);
                              setFieldValue('business_type', []);
                              setFieldValue('customer_market_type.id', val._id);
                              setFieldValue(
                                'customer_market_type.code',
                                val.code,
                              );
                            }}
                            isRequired
                            extra_values={[{ code: 'Select All' }]}
                          />
                        </FieldRow>
                      </View>
                    </Layout.FormGroup>
                    <RenderIf
                      condition={['sales_order'].includes(
                        props.data.applicable_for,
                      )}>
                      <Layout.FormGroup>
                        <View style={styles.formBottom}>
                          <FieldRow>
                            <SelectBox
                              label="Business Type"
                              options={businessTypes}
                              value={
                                values?.business_type?.length
                                  ? values.business_type
                                  : ''
                              }
                              multiple={true}
                              placeholder="Select Business Type"
                              trackBy="id"
                              labelKey="code"
                              editable={
                                values.customer_market_type?.code ? true : false
                              }
                              isRequired
                              onChange={(value) =>
                                setFieldValue('business_type', value)
                              }
                            />
                          </FieldRow>
                        </View>
                      </Layout.FormGroup>
                    </RenderIf>
                  </RenderIf>
                  <RenderIf
                    condition={['skp_approval'].includes(
                      props.data.applicable_for,
                    )}>
                    <Layout.FormGroup>
                      <View style={styles.formBottom}>
                        <FieldRow>
                          <SelectBox
                            label="Type"
                            options={CUSTOMER_TYPE}
                            value={values.customer_type}
                            placeholder="Select Type"
                            trackBy="value"
                            labelKey="label"
                            isRequired
                            onChange={(value) =>
                              setFieldValue('customer_type', value.value)
                            }
                          />
                        </FieldRow>
                      </View>
                    </Layout.FormGroup>
                  </RenderIf>
                  <RenderIf
                    condition={['budget_pocket'].includes(
                      props.data.applicable_for,
                    )}>
                    <Layout.FormGroup>
                      <View style={styles.formBottom}>
                        <FieldRow>
                          <SelectBox
                            label="Budget Pocket"
                            options={budget_pocket_type}
                            value={values.applicable_on}
                            placeholder="Select Budget Pocket"
                            trackBy="value"
                            labelKey="name"
                            isRequired
                            hideSearch
                            onChange={(value) =>
                              setFieldValue('applicable_on', value.value)
                            }
                          />
                        </FieldRow>
                      </View>
                    </Layout.FormGroup>
                  </RenderIf>
                  <RenderIf condition={props.data?.type == 'amount'}>
                    <Layout.FormGroup>
                      <View style={styles.row}>
                        <View style={[styles.flex1, styles.leftAmount]}>
                          <Input
                            label={initialValues.min_amount_label}
                            value={values.min_amount}
                            keyboardType="numeric"
                            onChangeText={(val) =>
                              setFieldValue('min_amount', val)
                            }
                            isRequired
                          />
                          <SociollaErrorMessage
                            error={errors['min_amount']}
                            visible={
                              (touched['min_amount'] || errors['min_amount']) &&
                              values.max_amount &&
                              values.min_amount
                            }
                          />
                        </View>
                        <View style={styles.flex1}>
                          <Input
                            label={initialValues.max_amount_label}
                            value={values.max_amount}
                            keyboardType="numeric"
                            onChangeText={(val) =>
                              setFieldValue('max_amount', val)
                            }
                            isRequired
                          />
                          <SociollaErrorMessage
                            error={errors['max_amount']}
                            visible={
                              (touched['max_amount'] || errors['max_amount']) &&
                              values.max_amount &&
                              values.min_amount
                            }
                          />
                        </View>
                      </View>
                    </Layout.FormGroup>
                  </RenderIf>
                  <RenderIf
                    condition={props.data.applicable_for === 'sales_order'}>
                    <Layout.FormGroup>
                      <View style={styles.formBottom}>
                        <FieldRow>
                          <Input
                            label={initialValues.min_amount_label}
                            value={values.min_amount}
                            keyboardType="numeric"
                            isNumeric
                            onChangeText={(val) =>
                              setFieldValue('min_amount', val)
                            }
                            isRequired
                          />
                        </FieldRow>
                        <SociollaErrorMessage
                          error={errors['min_amount']}
                          visible={
                            (touched['min_amount'] || errors['min_amount']) &&
                            values.min_amount
                          }
                        />
                      </View>
                    </Layout.FormGroup>
                    <Layout.FormGroup>
                      <View style={styles.formBottom}>
                        <FieldRow>
                          <SelectBox
                            label="Payment Overdue"
                            options={YES_NO_STATUS}
                            value={
                              values.payment_overdue
                                ? YES_NO_STATUS[0]
                                : YES_NO_STATUS[1]
                            }
                            placeholder=""
                            trackBy="value"
                            labelKey="name"
                            isRequired
                            onChange={(value) =>
                              setFieldValue('payment_overdue', value.value)
                            }
                          />
                        </FieldRow>
                      </View>
                    </Layout.FormGroup>
                  </RenderIf>
                  <RenderIf
                    condition={props.data.applicable_for === 'sales_activity'}>
                    <Layout.FormGroup>
                      <View style={styles.formBottom}>
                        <FieldRow>
                          <SelectBox
                            label="Tier"
                            options={TIER_TYPE_OPTIONS}
                            value={TIER_TYPE_OPTIONS.find(
                              (o) => o.value === values.tier_type,
                            )}
                            placeholder="Select Tier"
                            trackBy="value"
                            labelKey="label"
                            isRequired
                            hideSearch
                            onChange={(value) =>
                              setFieldValue('tier_type', value.value)
                            }
                          />
                        </FieldRow>
                        <SociollaErrorMessage
                          error={errors['tier_type']}
                          visible={touched['tier_type'] || errors['tier_type']}
                        />
                      </View>
                    </Layout.FormGroup>
                  </RenderIf>
                  {props.data.type == 'role' && (
                    <>
                      <RenderIf condition={tier?.listing?.length > 0}>
                        <Layout.FormGroup>
                          <FieldRow>
                            <SelectBox
                              label={tier?.yup?.applicable_on_label || ''}
                              options={tier.listing || []}
                              value={values.applicable_on}
                              placeholder={
                                'Select ' + tier.yup?.applicable_on_label || ''
                              }
                              trackBy="value"
                              labelKey="label"
                              multiple={false}
                              onChange={(value) =>
                                setFieldValue('applicable_on', value)
                              }
                              isRequired
                              hideSearch
                            />
                          </FieldRow>
                        </Layout.FormGroup>
                      </RenderIf>
                      <RenderIf
                        condition={
                          props.data.applicable_for === 'customer_cluster'
                        }>
                        <Layout.FormGroup>
                          <FieldRow>
                            <CustomerClusterSearch
                              label={initialValues.applicable_on_label}
                              value={values.applicable_on}
                              onChange={(value) =>
                                setFieldValue('applicable_on', value)
                              }
                              isRequired
                            />
                          </FieldRow>
                        </Layout.FormGroup>
                      </RenderIf>
                      <RenderIf
                        condition={
                          !tier.listing &&
                          props.data.applicable_for != 'customer_cluster'
                        }>
                        <Layout.FormGroup>
                          <FieldRow>
                            <RoleSearch
                              label={initialValues.applicable_on_label}
                              value={values.applicable_on}
                              onChange={(value) =>
                                setFieldValue('applicable_on', value)
                              }
                              isRequired
                            />
                          </FieldRow>
                        </Layout.FormGroup>
                      </RenderIf>
                      <SociollaErrorMessage
                        error={errors['applicable_on']}
                        visible={
                          touched['applicable_on'] || errors['applicable_on']
                        }
                      />
                    </>
                  )}
                </Layout.FormWrapper>

                <FieldArray name="approver">
                  {(formikArray) => (
                    <>
                      {approvers.map((ap, idx) => (
                        <Layout.FormWrapper
                          key={'approval-' + idx}
                          style={{ marginBottom: 15, flexDirection: 'column' }}>
                          <Layout.FormGroup>
                            <FieldRow
                              actionSlot={
                                idx > 0 ? (
                                  <TouchableOpacity
                                    onPress={() => {
                                      const newApprovers = values.approver
                                        .filter((_, i) => i !== idx)
                                        .map((ap, i) => ({
                                          ...ap,
                                          level: i + 1,
                                        }));
                                      setFieldValue('approver', newApprovers);
                                      removeApprover(idx);
                                    }}>
                                    <Image
                                      style={styles.removeIcon}
                                      width={16}
                                      height={16}
                                      source={require('@assets/img/icons/trash-grey.svg')}
                                      svg={true}
                                    />
                                  </TouchableOpacity>
                                ) : null
                              }>
                              <RoleSearch
                                label={ap.label}
                                value={values.approver[idx]?.role || ''}
                                onChange={(val) => {
                                  const payload = {
                                    level: ap.level,
                                    role: val,
                                  };
                                  setFieldValue(`approver.${idx}`, payload);
                                }}
                                customFilter={customFilterApprover}
                                isRequired
                              />
                            </FieldRow>
                          </Layout.FormGroup>
                          <SociollaErrorMessage
                            error={
                              errors.approver && errors.approver[idx]?.role
                                ? ap.label + ' is a required field'
                                : false
                            }
                            visible={
                              (touched.approver
                                ? touched.approver[idx]?.role
                                : false) ||
                              (errors.approver && errors.approver[idx]?.role)
                            }
                          />
                        </Layout.FormWrapper>
                      ))}
                      {approvers.length < 5 && (
                        <TouchableOpacity
                          style={styles.addApprovalButton}
                          onPress={() => {
                            formikArray.push({
                              level: approvers.length + 1,
                              role: '',
                            });
                            addApprover();
                          }}>
                          <SociollaText
                            color={Colors.blue0}
                            style={styles.addAproval}>
                            Add Approval
                          </SociollaText>
                        </TouchableOpacity>
                      )}
                    </>
                  )}
                </FieldArray>

                <Button
                  buttonStyle={styles.updateButton}
                  onPress={handleSubmit}
                  disabled={Object.entries(errors).length > 1}
                  title={
                    <SociollaText type="medium" style={{ color: Colors.white }}>
                      {isEdit ? 'Update' : 'Add'}
                    </SociollaText>
                  }
                />
              </>
            )}
          </Formik>
        </ScrollView>
      </View>
    </ModalFull>
  );
};

const styles = StyleSheet.create({
  addAproval: {
    fontSize: 12,
    marginTop: 20,
    textAlign: 'right',
  },
  addApprovalButton: {
    width: '100%',
    marginTop: -20,
  },
  container: {
    padding: 20,
    height: 'calc(100vh - 90px)',
  },
  flex1: {
    flex: 1,
  },
  formBottom: {
    marginBottom: 10,
  },
  leftAmount: {
    marginRight: isWeb ? 10 : 15,
  },
  updateButton: {
    width: '100%',
    marginTop: 30,
  },
  removeIcon: {
    width: 12,
    height: 12,
  },
  row: {
    flexDirection: 'row',
    width: '100%',
  },
});

ModalTier.propTypes = {
  show: PropTypes.bool,
  data: PropTypes.object,
  index: PropTypes.number,
  onClose: PropTypes.func,
  handleTier: PropTypes.func,
  cluster: PropTypes.array,
};

ModalTier.defaultProps = {
  show: false,
  data: {},
  index: -1,
  onClose: () => false,
  handleTier: () => false,
  cluster: [],
};

export default ModalTier;
